import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Button } from 'antd';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
  actionDimensionIds,
  DEVICES,
  MATOMO_ACTION,
  MATOMO_CATEGORY,
  MATOMO_NAME,
  staticUiLabels,
  TRACK_VALUE
} from '../../../../common/constants';
import useDevice from '../../../../common/useDevice';
import { secondsToMinute } from '../../../../common/utils';
import { gaEvent } from '../../../../lib/gtag';
import { useApp } from '../../../../providers/AppContext';
import FeaturedCollectionLoading from '../../../skeleton/FeaturedCollectionLoading';

const BlurHashImage = dynamic(() => import('../../../BlurHashImage'), {
  loading: () => null,
  ssr: false
});

function FeaturedCollection({
  title,
  img,
  mobileThumbnail,
  blurhash,
  videoCount,
  date,
  time,
  background = 'bg-n-700',
  description,
  buttonStyle = 'cs-button',
  button,
  titleStyle = 'text-white-90',
  countStyle = 'text-white-70',
  descriptionStyle = 'text-white-90',
  href,
  showCta,
  instanceData,
  isNewShow = false,
  isCarousel = false
}) {
  const {
    state: { currentUser, uiLabels }
  } = useApp();
  const router = useRouter();
  const { trackEvent } = useMatomo();
  const { showImage = true } = showCta;

  const deviceHook = useDevice();
  const [device, setDevice] = useState(DEVICES.DESKTOP);
  useEffect(() => {
    setDevice(deviceHook);
  }, [deviceHook]);

  const imageSrc =
    device === DEVICES.MOBILE && mobileThumbnail ? mobileThumbnail : img;

  const handleAnalytics = (category, action) => {
    trackEvent({
      category,
      action,
      name: JSON.stringify({
        action,
        email: currentUser?.email ?? '',
        instanceData
      }),
      value: TRACK_VALUE.SUCCESS
    });
    gaEvent({
      category,
      action,
      name: JSON.stringify({
        action,
        userId: currentUser?.id ?? '',
        instanceData
      }),
      value: TRACK_VALUE.SUCCESS
    });
    if (href) router.push(href);
  };

  const hasImage = showImage && imageSrc;

  return (
    <section className="featured-collection">
      <div className="feature-collection-card-v2">
        <div className="feature-collection-v2-content">
          <div
            className={`feature-collection-v2-img ${
              !hasImage ? background : ''
            }`}
          >
            {hasImage && (
              <>
                <BlurHashImage
                  src={imageSrc}
                  blurhash={blurhash}
                  fill
                  alt={title}
                  className="cover"
                />
                <div className="feature-collection-gradient-overlay" />
              </>
            )}
          </div>
          <div className="feature-collection-v2-text">
            {title && <h2 className={`heading ${titleStyle}`}>{title}</h2>}
            {description && (
              <p className={`description ${descriptionStyle}`}>{description}</p>
            )}
            {(videoCount !== undefined ||
              date?.trim() ||
              time ||
              isNewShow) && (
              <div className="feature-collection-meta">
                {isNewShow && (
                  <>
                    <span className={`date ${countStyle}`}>
                      {uiLabels?.FEATURED_COLLECTION_NEW_SHOW_TEXT?.value ||
                        staticUiLabels.FEATURED_COLLECTION_NEW_SHOW_TEXT}
                    </span>
                    {videoCount !== undefined || date?.trim() || time ? (
                      <span className="meta-separator" />
                    ) : null}
                  </>
                )}
                {videoCount !== undefined && (
                  <span className={`date ${countStyle}`}>
                    {videoCount} Items
                  </span>
                )}
                {videoCount !== undefined && (date?.trim() || time) && (
                  <span className="meta-separator" />
                )}
                {date?.trim() && (
                  <span className={`date ${countStyle}`}>{date}</span>
                )}
                {date?.trim() && time && <span className="meta-separator" />}
                {time && (
                  <span className={`date ${countStyle}`}>
                    {secondsToMinute(time)}
                  </span>
                )}
              </div>
            )}

            {(button ||
              uiLabels?.FEATURED_COLLECTION_VIEW_MORE_TEXT?.value ||
              staticUiLabels.FEATURED_COLLECTION_VIEW_MORE_TEXT) && (
              <div className="slider-button">
                <Button
                  onClick={() => {
                    const buttonText =
                      button ||
                      uiLabels?.FEATURED_COLLECTION_VIEW_MORE_TEXT?.value ||
                      staticUiLabels.FEATURED_COLLECTION_VIEW_MORE_TEXT;
                    handleAnalytics(
                      MATOMO_CATEGORY.COLLECTION,
                      MATOMO_ACTION.FEATURED_COLLECTION_BTN_CLICKED
                    );
                    trackEvent({
                      category: MATOMO_CATEGORY.BUTTON,
                      action: MATOMO_ACTION.CLICK,
                      name: MATOMO_NAME.BUTTON_CLICK,
                      customDimensions: [
                        {
                          id: actionDimensionIds.cta_title,
                          value: buttonText
                        },
                        {
                          id: actionDimensionIds.page_source,
                          value: router.asPath
                        }
                      ],
                      value: TRACK_VALUE.SUCCESS
                    });
                  }}
                  size="large"
                  type="primary"
                  className={`cs-button ${buttonStyle}`}
                  tabIndex={isCarousel ? '-1' : undefined}
                >
                  {button ||
                    uiLabels?.FEATURED_COLLECTION_VIEW_MORE_TEXT?.value ||
                    staticUiLabels.FEATURED_COLLECTION_VIEW_MORE_TEXT}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

FeaturedCollection.Loading = function Loading() {
  return <FeaturedCollectionLoading />;
};

export default FeaturedCollection;
